import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ContentHistory,
  PublicationFeedback,
  PublicationFeedbackActivity,
  PublicationFeedbackState,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';

import {
  PublicationFeedbackTypeSeverity,
  isFeedbackClosed,
  publicationFeedbackTypeDisplayName,
} from '../utils/DisplayNames';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { log } from '../../loggers/Logger';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';
import { useCurrentFeedbackId } from '../v2-hooks/useUrlHash';
import { useCoAuthors } from '../v2-hooks/useCoAuthors';
import { useCurrentUser } from '../../contexts/UserContext';

const SEVERITY_PRIORITY: Record<PublicationFeedbackTypeSeverity, number> = {
  High: 3,
  Medium: 2,
  Low: 1,
  NA: 0,
};

function severityOf(feedback: PublicationFeedback): PublicationFeedbackTypeSeverity {
  const issueType = feedback.publicationFeedbackData?.issueType;
  const display = issueType ? publicationFeedbackTypeDisplayName.get(issueType) : undefined;
  return display?.severity ?? 'NA';
}

/** Sorts feedback by severity (High→Low) then most-recently-updated first. */
function sortBySeverityAndTime(feedbacks: readonly PublicationFeedback[]): PublicationFeedback[] {
  return [...feedbacks].sort((a, b) => {
    const severityDelta = SEVERITY_PRIORITY[severityOf(b)] - SEVERITY_PRIORITY[severityOf(a)];
    if (severityDelta !== 0) return severityDelta;
    return (b.lastUpdatedAt?.toMillis() ?? 0) - (a.lastUpdatedAt?.toMillis() ?? 0);
  });
}

/** Workflow states in which a version is the one readers currently see. */
const LIVE_STATES: ReadonlySet<WorkflowState> = new Set([
  WorkflowState.PUBLISHED,
  WorkflowState.FINALIZED,
  WorkflowState.DISTRIBUTED,
]);

/** A published version of the content, as listed in its workflow history. */
export interface PublishedVersion {
  readonly publicationDate: DateTime;
  /** True for the newest version that is currently live. */
  readonly isLatest: boolean;
  /** True when this version has been unpublished. */
  readonly isUnpublished: boolean;
}

/** Indexes the content's workflow history by workflowId, marking the newest live version. */
function toPublishedVersions(
  workflows: readonly ContentHistory[],
): ReadonlyMap<number, PublishedVersion> {
  const liveIds = workflows.filter((w) => LIVE_STATES.has(w.state)).map((w) => w.workflowId);
  const latestId = liveIds.length > 0 ? Math.max(...liveIds) : undefined;
  return new Map(
    workflows
      .filter((w) => w.publicationDate)
      .map((w) => [
        w.workflowId,
        {
          publicationDate: w.publicationDate,
          isLatest: w.workflowId === latestId,
          isUnpublished: w.state === WorkflowState.UNPUBLISHED,
        },
      ]),
  );
}

/** A change to apply to one feedback item after a server update. */
export interface FeedbackUpdate {
  readonly feedbackId: string;
  /** New state, when the update changed status. */
  readonly newState?: PublicationFeedbackState;
  /** Activity produced by the update, appended to the item's timeline. */
  readonly activity?: PublicationFeedbackActivity;
}

/** Read-only presentation state for the Update Requests tab. */
export interface UpdateRequestsTabViewModel {
  /** Content receiving the update request. */
  readonly contentId: string;
  readonly isLoading: boolean;
  readonly loadError: string | undefined;
  /** Feedback items to display, already filtered by the open/closed toggle and sorted. */
  readonly feedbacks: readonly PublicationFeedback[];
  /** True when there are no items to display under the current filter. */
  readonly isEmpty: boolean;
  /** Whether the current user is the content's owner or a coauthor. */
  readonly isAuthor: boolean;
  /** Published versions by workflowId; empty until (or if) the history fails to load. */
  readonly publishedVersions: ReadonlyMap<number, PublishedVersion>;
  /** Whether closed (resolved) requests are included. */
  readonly isShowingClosed: boolean;
  /** Toggle inclusion of closed requests. */
  readonly setShowClosed: (show: boolean) => void;
  /** Feedback id to scroll to / highlight, derived from the URL hash; `undefined` when none. */
  readonly highlightFeedbackId: string | undefined;
  /** Reload the feedback list. */
  readonly reload: () => void;
  /**
   * Patch one item in place after a server-side update — applies the new state
   * and appends the activity. Keeps the parent list authoritative so toggling
   * the filter or re-sorting never reverts to stale item data.
   */
  readonly applyFeedbackUpdate: (update: FeedbackUpdate) => void;
}

/**
 * Loads and presents publication feedback (update requests) for the loaded
 * content. Sorts by severity then recency, supports an open/closed filter, and
 * surfaces a deep-link target from the `#feedback-<id>` URL hash.
 * Returns undefined while no content is loaded — the view renders nothing
 * during that transient state rather than crashing.
 */
export function useUpdateRequestsTabViewModel(): UpdateRequestsTabViewModel | undefined {
  const service = useContentEditorService();
  const mpl = useMplServiceClient();
  const document = useLoadedDocument();
  const { userId, canSeeActivityForContent } = useCurrentUser();
  const coAuthors = useCoAuthors();

  const contentId = document?.contentId;
  // The history leaves out unpublished versions unless asked, and only authors and staff roles may ask.
  const includeUnpublished =
    document !== undefined &&
    canSeeActivityForContent(
      document.author?.userId,
      coAuthors.map((c) => c.userId),
    );

  const highlightFeedbackId = useCurrentFeedbackId();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [allFeedbacks, setAllFeedbacks] = useState<readonly PublicationFeedback[]>([]);
  const [isShowingClosed, setIsShowingClosed] = useState(false);
  const [publishedVersions, setPublishedVersions] = useState<ReadonlyMap<number, PublishedVersion>>(
    new Map(),
  );

  const cancelRef = useRef<(() => void) | undefined>(undefined);
  const hasAutoRevealedRef = useRef(false);

  const reload = useCallback(() => {
    if (!contentId) return;
    cancelRef.current?.();
    let cancelled = false;
    cancelRef.current = () => {
      cancelled = true;
    };

    setIsLoading(true);
    setLoadError(undefined);
    mpl
      .listPublicationFeedbacks(contentId)
      .then(({ publicationFeedbacks }) => {
        if (cancelled) return;
        setAllFeedbacks(publicationFeedbacks);
      })
      .catch((err) => {
        if (cancelled) return;
        log.error({ err, contentId }, 'Failed to load update requests');
        setLoadError('Failed to load update requests.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
  }, [contentId, mpl]);

  // When the tab mounts, then load the feedback list; cancel on unmount.
  useEffect(() => {
    reload();
    return () => cancelRef.current?.();
  }, [reload]);

  // When the tab mounts, then load the version history for the version labels.
  // A failure only hides the publish dates, so it is logged rather than surfaced.
  useEffect(() => {
    if (!contentId) return;
    let cancelled = false;
    mpl
      .listWorkflowHistory(contentId, { includeUnpublished })
      .then(({ workflows }) => {
        if (!cancelled) setPublishedVersions(toPublishedVersions(workflows));
      })
      .catch((err) => log.warn({ err, contentId }, 'Failed to load version history'));
    return () => {
      cancelled = true;
    };
  }, [contentId, mpl, includeUnpublished]);

  // When the deep-link target is a closed item, then reveal closed requests
  // once so it renders under the filter and the view can scroll it into view.
  // Guarded by a ref so later feedback updates never override a manual toggle.
  useEffect(() => {
    if (isLoading || !highlightFeedbackId || hasAutoRevealedRef.current) return;
    hasAutoRevealedRef.current = true;
    const target = allFeedbacks.find((f) => f.feedbackId === highlightFeedbackId);
    if (target && isFeedbackClosed(target.state)) setIsShowingClosed(true);
  }, [highlightFeedbackId, isLoading, allFeedbacks]);

  const applyFeedbackUpdate = useCallback((update: FeedbackUpdate) => {
    setAllFeedbacks((prev) =>
      prev.map((feedback) => {
        if (feedback.feedbackId !== update.feedbackId) return feedback;
        const next = { ...feedback };
        if (update.newState) next.state = update.newState;
        if (update.activity) next.activities = [...(feedback.activities ?? []), update.activity];
        return next;
      }),
    );
  }, []);

  const feedbacks = useMemo(() => {
    const visible = isShowingClosed
      ? allFeedbacks
      : allFeedbacks.filter((f) => !isFeedbackClosed(f.state));
    return sortBySeverityAndTime(visible);
  }, [allFeedbacks, isShowingClosed]);

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!document) return undefined;

  return {
    contentId: document.contentId,
    isLoading,
    loadError,
    feedbacks,
    isEmpty: !isLoading && feedbacks.length === 0,
    isAuthor: userId === document.author?.userId || coAuthors.some((c) => c.userId === userId),
    publishedVersions,
    isShowingClosed,
    setShowClosed: setIsShowingClosed,
    highlightFeedbackId,
    reload,
    applyFeedbackUpdate,
  };
}
